import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useOrders = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [factories, setFactories] = useState<any[]>([]);
  const [tailors, setTailors] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [orderStatuses, setOrderStatuses] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchOrderStatuses = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_enum_values', { enum_name: 'order_status' });
      
      if (error) {
        console.error("Error fetching order statuses:", error);
        // Fallback to default statuses if the RPC call fails
        setOrderStatuses(["pending", "in_progress", "completed", "canceled"]);
      } else {
        // Ensure data is an array of strings
        const statusArray = Array.isArray(data) ? data : [];
        setOrderStatuses(statusArray);
      }
    } catch (error) {
      console.error("Error fetching order statuses:", error);
      // Fallback to default statuses
      setOrderStatuses(["pending", "in_progress", "completed", "canceled"]);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log("Fetching order data...");
      
      // Fetch order statuses first
      await fetchOrderStatuses();
      
      // Fetch data in parallel to improve performance
      const [ordersResult, customersResult, factoriesResult, tailorsResult, categoriesResult] = await Promise.all([
        // Filter out orders with "saved" and "draft" status
        supabase
          .from("orders")
          .select("id, order_number, customer_id, factory_id, tailor_id, product_id, status, total_amount, notes, created_at")
          .not("status", "in", "(saved,draft)")
          .order("created_at", { ascending: false })
          .limit(100), // Limit results to prevent timeout
        supabase.from("customers").select("id, customer_name, customer_email, customer_phone, customer_address"),
        supabase.from("factories").select("id, name").eq("active", true),
        supabase.from("users").select("id, email, profile").eq("role", "tailor"),
        supabase.from("products").select("id, name").order("name")
      ]);
      
      // Check for errors in any of the queries
      if (ordersResult.error) throw ordersResult.error;
      if (customersResult.error) throw customersResult.error;
      if (factoriesResult.error) throw factoriesResult.error;
      if (tailorsResult.error) throw tailorsResult.error;
      if (categoriesResult.error) throw categoriesResult.error;
      
      console.log("Basic orders fetched:", ordersResult.data?.length || 0);
      
      // Create lookup maps for each entity type
      const customerMap = new Map(customersResult.data?.map(c => [c.id, c]) || []);
      const factoryMap = new Map(factoriesResult.data?.map(f => [f.id, f]) || []);
      const tailorMap = new Map(tailorsResult.data?.map(t => [t.id, t]) || []);
      const categoryMap = new Map(categoriesResult.data?.map(cat => [cat.id, cat]) || []);
      
      // Enrich order data with related entities
      const enrichedOrders = ordersResult.data?.map(order => ({
        ...order,
        customer: customerMap.get(order.customer_id) || null,
        factory: factoryMap.get(order.factory_id) || null,
        tailor: tailorMap.get(order.tailor_id) || null,
        category: categoryMap.get(order.product_id) || null
      })) || [];
      
      console.log("Enriched orders:", enrichedOrders.length);
      
      setOrders(enrichedOrders);
      setCustomers(customersResult.data || []);
      setTailors(tailorsResult.data || []);
      setFactories(factoriesResult.data || []);
      setCategories(categoriesResult.data || []);
      
    } catch (error: any) {
      console.error("Error fetching data:", error);
      toast({
        title: "Error fetching data",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createOrder = async (orderData: any) => {
    try {
      const { error } = await supabase
        .from("orders")
        .insert({
          order_number: orderData.order_number,
          customer_id: orderData.customer_id === "none" ? null : orderData.customer_id,
          factory_id: orderData.factory_id === "none" ? null : orderData.factory_id,
          tailor_id: orderData.tailor_id === "none" ? null : orderData.tailor_id,
          product_id: orderData.category_id === "none" ? null : orderData.category_id,
          status: orderData.status,
          total_amount: Number(orderData.total_amount),
          notes: orderData.notes || null
        });

      if (error) throw error;
      
      toast({
        title: "Order Created",
        description: `Order ${orderData.order_number} has been created.`
      });
      
      await fetchData();
      return true;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const updateOrder = async (id: string, orderData: any) => {
    try {
      const { error } = await supabase
        .from("orders")
        .update({
          order_number: orderData.order_number,
          customer_id: orderData.customer_id === "none" ? null : orderData.customer_id,
          factory_id: orderData.factory_id === "none" ? null : orderData.factory_id,
          tailor_id: orderData.tailor_id === "none" ? null : orderData.tailor_id,
          product_id: orderData.category_id === "none" ? null : orderData.category_id,
          status: orderData.status,
          total_amount: Number(orderData.total_amount),
          notes: orderData.notes || null,
          updated_at: new Date().toISOString()
        })
        .eq("id", id);

      if (error) throw error;
      
      toast({
        title: "Order Updated",
        description: `Order ${orderData.order_number} has been updated.`
      });
      
      await fetchData();
      return true;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteOrder = async (id: string) => {
    try {
      const { error } = await supabase
        .from("orders")
        .delete()
        .eq("id", id);

      if (error) throw error;
      
      await fetchData();
      return true;
    } catch (error: any) {
      toast({
        title: "Error deleting order",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return {
    orders,
    customers,
    factories,
    tailors,
    categories,
    orderStatuses,
    loading,
    fetchData,
    createOrder,
    updateOrder,
    deleteOrder
  };
};
